import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
// Minimal coordinate presence
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-2530" />;
export function MirrorsLanding() {
  return <>
      <Helmet data-id="element-2531">
        <title data-id="element-2532">Mirrors — Continuous Interpretations | Lazy Monk Labs</title>
        <meta name="description" content="Mirrors are continuous interpretations. Observe how a pattern actually unfolds over time, across real situations." data-id="element-2533" />
        <meta name="robots" content="index, follow" data-id="element-2534" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2535">
        {/* Navigation */}
        <header data-id="element-2536">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2537">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2538">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-2539">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-2540" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-2541">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-2542">
                <Link to="/decodes" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-medium" data-id="element-2543">
                  Decodes
                </Link>
                <Link to="/decoder" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-2544">
                  Decoder
                </Link>
                <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-2545">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-2546">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-2547">
          {/* 1. HERO */}
          <section className="relative py-32 md:py-40 px-6 overflow-hidden" data-id="element-2548">
            <CoordinateGrid data-id="element-2549" />
            <div className="relative z-10 max-w-5xl mx-auto space-y-12" data-id="element-2550">
              <div className="space-y-6" data-id="element-2551">
                <h1 className="text-5xl md:text-7xl font-serif leading-none text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-2552">
                  Mirrors are continuous interpretations.
                </h1>

                <p className="text-xl md:text-2xl text-gray-400 font-light leading-relaxed max-w-3xl" data-id="element-2553">
                  A Mirror lets you observe how a pattern actually unfolds over
                  time, across real situations — using the same engine that
                  powers all Decodes at Lazy Monk Labs.
                </p>
              </div>

              <div className="pt-8 flex flex-col sm:flex-row gap-4" data-id="element-2554">
                <Link to="/mirrors/work" className="px-10 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-sm font-medium hover:bg-gray-200 transition-colors text-center" data-id="element-2555">
                  Explore Work Mirror
                </Link>
                <Link to="/decoder" className="px-10 py-4 border border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors text-center" data-id="element-2556">
                  Learn about the engine (Decoder)
                </Link>
              </div>
            </div>
          </section>

          {/* 2. WHAT IS A MIRROR */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-2557">
            <div className="max-w-4xl mx-auto" data-id="element-2558">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2559">
                What is a Mirror?
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-2560">
                <p data-id="element-2561">
                  A Mirror is not a report. And not a one-time interpretation.
                </p>

                <p data-id="element-2562">
                  A Mirror is a living system that continuously interprets your
                  underlying pattern as it shows up in real life.
                </p>

                <div className="pt-6 border-t border-[#2B2B2B] space-y-6" data-id="element-2563">
                  <p className="text-gray-500" data-id="element-2564">Instead of asking:</p>
                  <p className="pl-6 border-l-2 border-[#2B2B2B]" data-id="element-2565">
                    "What is my pattern?"
                  </p>

                  <p className="text-gray-500 pt-4" data-id="element-2566">A Mirror lets you watch:</p>
                  <p className="pl-6 border-l-2 border-[#2B2B2B] text-[#F5F5F5]" data-id="element-2567">
                    "How does this pattern keep playing out?"
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 3. WHAT MIRRORS ARE FOR */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-2568">
            <div className="max-w-4xl mx-auto" data-id="element-2569">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2570">
                When do people use a Mirror?
              </h2>

              <div className="space-y-6 text-base text-gray-400" data-id="element-2571">
                <p data-id="element-2572">• "I want to see my pattern across weeks and months."</p>
                <p data-id="element-2573">• "I want to see the loops I keep repeating at work."</p>
                <p data-id="element-2574">
                  • "I want a record of how I actually operate under pressure."
                </p>
                <p data-id="element-2575">• "I want to observe, not just understand."</p>
              </div>
            </div>
          </section>

          {/* 4. MIRRORS VS DECODES */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-2576">
            <div className="max-w-6xl mx-auto" data-id="element-2577">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-16 text-center" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2578">
                Mirrors and Decodes
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12" data-id="element-2579">
                <div className="space-y-6" data-id="element-2580">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-2581">
                    Decodes
                  </h3>
                  <div className="space-y-3 text-base text-gray-400" data-id="element-2582">
                    <p data-id="element-2583">• One-time interpretations</p>
                    <p data-id="element-2584">• Good for clarity now</p>
                    <p data-id="element-2585">• Point-in-time view</p>
                    <p data-id="element-2586">• Answer: "What is the structure?"</p>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-2587">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-2588">
                    Mirrors
                  </h3>
                  <div className="space-y-3 text-base text-gray-400" data-id="element-2589">
                    <p data-id="element-2590">• Continuous interpretations</p>
                    <p data-id="element-2591">• Good for long-term observation</p>
                    <p data-id="element-2592">• Time-based view</p>
                    <p data-id="element-2593">• Answer: "How does this structure keep showing up?"</p>
                  </div>
                </div>
              </div>

              <div className="text-center" data-id="element-2594">
                <p className="text-lg text-[#F5F5F5]" data-id="element-2595">
                  Same engine. Different time horizons.
                </p>
              </div>
            </div>
          </section>

          {/* 5. HOW MIRRORS WORK */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-2596">
            <div className="max-w-4xl mx-auto" data-id="element-2597">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2598">
                How a Mirror works
              </h2>

              <div className="space-y-12" data-id="element-2599">
                {/* You */}
                <div className="space-y-4" data-id="element-2600">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-2601">
                    You
                  </p>
                  <div className="space-y-3 text-base text-gray-400" data-id="element-2602">
                    <p data-id="element-2603">• Log situations</p>
                    <p data-id="element-2604">• Log days</p>
                    <p data-id="element-2605">• Log modes</p>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex justify-center" data-id="element-2606">
                  <div className="text-gray-600 text-2xl" data-id="element-2607">↓</div>
                </div>

                {/* Mirror */}
                <div className="space-y-4" data-id="element-2608">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-2609">
                    Mirror
                  </p>
                  <p className="text-base text-gray-400" data-id="element-2610">
                    • Continuously interprets these through your underlying
                    pattern
                  </p>
                </div>

                {/* Arrow */}
                <div className="flex justify-center" data-id="element-2611">
                  <div className="text-gray-600 text-2xl" data-id="element-2612">↓</div>
                </div>

                {/* You */}
                <div className="space-y-4" data-id="element-2613">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-2614">
                    You
                  </p>
                  <div className="space-y-3 text-base text-gray-400" data-id="element-2615">
                    <p data-id="element-2616">• See recurring loops</p>
                    <p data-id="element-2617">• See structural tendencies</p>
                    <p data-id="element-2618">• See what actually repeats</p>
                  </div>
                </div>

                {/* Closing line */}
                <div className="pt-8 border-t border-[#2B2B2B]" data-id="element-2619">
                  <p className="text-sm text-gray-600" data-id="element-2620">
                    This is not journaling. This is pattern recording.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 6. AVAILABLE MIRRORS */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-2621">
            <div className="max-w-4xl mx-auto" data-id="element-2622">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-16" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2623">
                Available Mirrors
              </h2>

              {/* Work Mirror Card */}
              <div className="border border-[#2B2B2B] p-10 space-y-8 bg-[#0A0A0A] mb-8" data-id="element-2624">
                <div className="space-y-4" data-id="element-2625">
                  <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-2626">
                    Work Mirror
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-2627">
                    A continuous mirror for how you operate around work,
                    responsibility, decisions, and pressure.
                  </p>
                </div>

                <Link to="/mirrors/work" className="block w-full px-6 py-3 border border-[#F5F5F5] text-[#F5F5F5] text-center text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-2628">
                  Explore Work Mirror
                </Link>
              </div>

              {/* Future mirrors note */}
              <div className="text-center" data-id="element-2629">
                <p className="text-sm text-gray-600" data-id="element-2630">
                  More mirrors (Life, Relationship, Team) will come later.
                </p>
              </div>
            </div>
          </section>

          {/* 7. WHY THIS EXISTS */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-2631">
            <div className="max-w-4xl mx-auto" data-id="element-2632">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2633">
                Why build Mirrors?
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-2634">
                <p data-id="element-2635">
                  Most patterns don't reveal themselves in a single moment. They
                  reveal themselves through repetition.
                </p>

                <p className="text-[#F5F5F5] pt-6 border-t border-[#2B2B2B]" data-id="element-2636">
                  Mirrors exist to make repetition visible.
                </p>
              </div>
            </div>
          </section>

          {/* 8. FINAL CTA */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-2637">
            <div className="max-w-4xl mx-auto text-center space-y-12" data-id="element-2638">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5]" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2639">
                Start with the Work Mirror
              </h2>

              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2640">
                <Link to="/mirrors/work" className="px-12 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-sm font-medium hover:bg-gray-200 transition-colors" data-id="element-2641">
                  Explore Work Mirror
                </Link>
                <Link to="/decodes" className="px-12 py-4 border border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-2642">
                  Explore Decodes
                </Link>
              </div>

              <div data-id="element-2643">
                <Link to="/" className="inline-flex items-center gap-3 text-sm text-gray-600 hover:text-gray-400 transition-colors group" data-id="element-2644">
                  <span data-id="element-2645">Back to Home</span>
                </Link>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-2646">
          <div className="max-w-6xl mx-auto" data-id="element-2647">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-2648">
              <div data-id="element-2649">
                <p className="text-sm text-gray-500 font-mono" data-id="element-2650">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-2651">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-2652">
                <Link to="/decodes" className="hover:text-[#F5F5F5] transition-colors" data-id="element-2653">
                  Decodes
                </Link>
                <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-2654">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-2655">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-2656">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-2657">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}